import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { ConfigFieldsProps } from "./types";

export function RelationshipTableConfig({
  editedComponent,
  updateConfig,
  availableComponents,
}: ConfigFieldsProps) {
  const tableComponents = availableComponents.filter(
    (c) => c.id !== editedComponent.id && c.type === "table",
  );

  const leftTableComponent = tableComponents.find(
    (c) => c.id === editedComponent.config.leftTable?.id,
  );
  const rightTableComponent = tableComponents.find(
    (c) => c.id === editedComponent.config.rightTable?.id,
  );

  const leftTableColumns = leftTableComponent?.config?.columns || [];
  const rightTableColumns = rightTableComponent?.config?.columns || [];

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="relationshipTitle">Título</Label>
        <Input
          id="relationshipTitle"
          type="text"
          value={editedComponent.config.title || ""}
          onChange={(e) => updateConfig("title", e.target.value)}
          placeholder="Relacionamentos"
        />
      </div>

      <div className="p-4 bg-accent border border-primary/20 rounded-md">
        <p className="text-sm mb-2">
          <strong>💡 Como funciona:</strong>
        </p>
        <p className="text-xs text-muted-foreground">
          Este componente relaciona duas tabelas existentes na página. Selecione
          as tabelas e os campos chave que serão usados para fazer a ligação.
        </p>
      </div>

      {/* Primeira Tabela */}
      <div className="space-y-2">
        <Label htmlFor="leftTable">Primeira Tabela</Label>
        <Select
          value={editedComponent.config.leftTable?.id || ""}
          onValueChange={(value) => {
            const selectedTable = tableComponents.find((c) => c.id === value);
            if (selectedTable) {
              const columns = selectedTable.config?.columns || [];
              const data = selectedTable.config?.dataSource?.data || [];
              updateConfig("leftTable", {
                id: selectedTable.id,
                name: selectedTable.config?.title || "Tabela A",
                data: data,
                keyField: columns[0]?.field || "id",
                displayField:
                  columns[1]?.field || columns[0]?.field || "name",
              });
            }
          }}
        >
          <SelectTrigger id="leftTable">
            <SelectValue placeholder="Selecione a primeira tabela" />
          </SelectTrigger>
          <SelectContent>
            {tableComponents.map((comp) => {
              const pageName = (comp as any)._pageName;
              const displayName = pageName
                ? `${comp.config.title || comp.type} (Página: ${pageName})`
                : `${comp.config.title || `Tabela - ${comp.id.substring(0, 8)}`}`;
              return (
                <SelectItem key={comp.id} value={comp.id}>
                  {displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {leftTableComponent && (
        <div className="ml-4 space-y-3 p-3 border rounded bg-muted/30">
          <div className="space-y-2">
            <Label htmlFor="leftKeyField">Campo Chave (ID)</Label>
            <Select
              value={editedComponent.config.leftTable?.keyField || ""}
              onValueChange={(value) => {
                updateConfig("leftTable", {
                  ...editedComponent.config.leftTable,
                  keyField: value,
                });
              }}
            >
              <SelectTrigger id="leftKeyField">
                <SelectValue placeholder="Selecione o campo ID" />
              </SelectTrigger>
              <SelectContent>
                {leftTableColumns.map((col: any) => (
                  <SelectItem key={col.field} value={col.field}>
                    {col.label} ({col.field})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Campo que identifica unicamente cada registro
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="leftDisplayField">Campo de Exibição</Label>
            <Select
              value={editedComponent.config.leftTable?.displayField || ""}
              onValueChange={(value) => {
                updateConfig("leftTable", {
                  ...editedComponent.config.leftTable,
                  displayField: value,
                });
              }}
            >
              <SelectTrigger id="leftDisplayField">
                <SelectValue placeholder="Selecione o campo a exibir" />
              </SelectTrigger>
              <SelectContent>
                {leftTableColumns.map((col: any) => (
                  <SelectItem key={col.field} value={col.field}>
                    {col.label} ({col.field})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Campo que será mostrado na tabela de relacionamento
            </p>
          </div>
        </div>
      )}

      {/* Segunda Tabela */}
      <div className="space-y-2">
        <Label htmlFor="rightTable">Segunda Tabela</Label>
        <Select
          value={editedComponent.config.rightTable?.id || ""}
          onValueChange={(value) => {
            const selectedTable = tableComponents.find((c) => c.id === value);
            if (selectedTable) {
              const columns = selectedTable.config?.columns || [];
              const data = selectedTable.config?.dataSource?.data || [];
              updateConfig("rightTable", {
                id: selectedTable.id,
                name: selectedTable.config?.title || "Tabela B",
                data: data,
                keyField: columns[0]?.field || "id",
                displayField:
                  columns[1]?.field || columns[0]?.field || "name",
              });
            }
          }}
        >
          <SelectTrigger id="rightTable">
            <SelectValue placeholder="Selecione a segunda tabela" />
          </SelectTrigger>
          <SelectContent>
            {tableComponents.map((comp) => {
              const pageName = (comp as any)._pageName;
              const displayName = pageName
                ? `${comp.config.title || comp.type} (Página: ${pageName})`
                : `${comp.config.title || `Tabela - ${comp.id.substring(0, 8)}`}`;
              return (
                <SelectItem key={comp.id} value={comp.id}>
                  {displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {rightTableComponent && (
        <div className="ml-4 space-y-3 p-3 border rounded bg-muted/30">
          <div className="space-y-2">
            <Label htmlFor="rightKeyField">Campo Chave (ID)</Label>
            <Select
              value={editedComponent.config.rightTable?.keyField || ""}
              onValueChange={(value) => {
                updateConfig("rightTable", {
                  ...editedComponent.config.rightTable,
                  keyField: value,
                });
              }}
            >
              <SelectTrigger id="rightKeyField">
                <SelectValue placeholder="Selecione o campo ID" />
              </SelectTrigger>
              <SelectContent>
                {rightTableColumns.map((col: any) => (
                  <SelectItem key={col.field} value={col.field}>
                    {col.label} ({col.field})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Campo que identifica unicamente cada registro
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rightDisplayField">Campo de Exibição</Label>
            <Select
              value={editedComponent.config.rightTable?.displayField || ""}
              onValueChange={(value) => {
                updateConfig("rightTable", {
                  ...editedComponent.config.rightTable,
                  displayField: value,
                });
              }}
            >
              <SelectTrigger id="rightDisplayField">
                <SelectValue placeholder="Selecione o campo a exibir" />
              </SelectTrigger>
              <SelectContent>
                {rightTableColumns.map((col: any) => (
                  <SelectItem key={col.field} value={col.field}>
                    {col.label} ({col.field})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Campo que será mostrado na tabela de relacionamento
            </p>
          </div>
        </div>
      )}

      {/* Campos Chave na Tabela de Relacionamento */}
      <div className="p-4 bg-accent border border-primary/20 rounded-md space-y-3">
        <p className="text-sm font-medium">Campos de Relacionamento</p>
        <p className="text-xs text-muted-foreground mb-3">
          Defina os nomes dos campos na tabela de relacionamento que irão
          referenciar as tabelas selecionadas.
        </p>

        <div className="space-y-2">
          <Label htmlFor="leftKeyField">
            Campo que referencia{" "}
            {editedComponent.config.leftTable?.name || "Primeira Tabela"}
          </Label>
          <Input
            id="leftKeyFieldName"
            type="text"
            value={editedComponent.config.leftKeyField || ""}
            onChange={(e) => updateConfig("leftKeyField", e.target.value)}
            placeholder="leftId"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rightKeyField">
            Campo que referencia{" "}
            {editedComponent.config.rightTable?.name || "Segunda Tabela"}
          </Label>
          <Input
            id="rightKeyFieldName"
            type="text"
            value={editedComponent.config.rightKeyField || ""}
            onChange={(e) => updateConfig("rightKeyField", e.target.value)}
            placeholder="rightId"
          />
        </div>
      </div>

      {tableComponents.length === 0 && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ Nenhuma tabela encontrada na página. Adicione pelo menos duas
            tabelas antes de criar relacionamentos.
          </p>
        </div>
      )}
    </>
  );
}
